import { useState } from 'react';
import { Conversations, type Poll } from '@/lib/api';

interface Props {
  conversationId: string;
  onClose: () => void;
  onCreated: (poll: Poll) => void;
}

export default function PollCreator({ conversationId, onClose, onCreated }: Props) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function setOption(i: number, value: string) {
    setOptions((prev) => prev.map((v, idx) => (idx === i ? value : v)));
  }
  function addOption() {
    if (options.length >= 10) return;
    setOptions((prev) => [...prev, '']);
  }
  function removeOption(i: number) {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, idx) => idx !== i));
  }

  const trimmed = options.map((o) => o.trim()).filter(Boolean);
  const canSubmit = question.trim().length > 0 && trimmed.length >= 2 && trimmed.length <= 10;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    try {
      const { poll } = await Conversations.createPoll(conversationId, {
        question: question.trim(),
        options: trimmed,
        allowMultiple,
      });
      onCreated(poll);
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed';
      setErr(m);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create poll"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card w-full max-w-md p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Create poll</h2>
          <button type="button" aria-label="Close" className="btn-ghost px-2" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <label className="mb-2 block">
          <span className="mb-1 block text-sm font-medium">Question</span>
          <input
            className="input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={200}
            placeholder="What's for lunch?"
          />
        </label>

        <div className="mb-2 space-y-2">
          {options.map((opt, i) => (
            <div key={i} className="flex gap-2">
              <input
                className="input"
                value={opt}
                placeholder={`Option ${i + 1}`}
                maxLength={100}
                onChange={(e) => setOption(i, e.target.value)}
              />
              <button
                type="button"
                aria-label={`Remove option ${i + 1}`}
                className="btn-ghost px-2"
                disabled={options.length <= 2}
                onClick={() => removeOption(i)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            className="btn-ghost"
            onClick={addOption}
            disabled={options.length >= 10}
          >
            + Add option
          </button>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allowMultiple}
              onChange={(e) => setAllowMultiple(e.target.checked)}
              className="h-4 w-4 accent-brand-500"
            />
            Allow multiple choices
          </label>
        </div>

        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/60 dark:text-red-300">{err}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={!canSubmit || submitting} onClick={submit}>
            {submitting ? 'Creating…' : 'Create poll'}
          </button>
        </div>
      </div>
    </div>
  );
}
